import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, AlertTriangle, CheckCircle, Clock, Wifi, WifiOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { mockCustomers } from "@/utils/customerGroups";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  type: string;
  customers: string[];
}

interface Customer {
  id: string;
  name: string;
}

interface TagAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceId?: string;  // Optional for single resource
  resourceIds?: string[];  // Optional for bulk resources
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user";
  resourceName?: string;
  onAssignmentComplete?: () => void;
  onComplete?: () => void;
}

export const TagAssignmentDialog = ({
  open,
  onOpenChange,
  resourceId,
  resourceIds,
  resourceType,
  resourceName = "",
  onAssignmentComplete,
  onComplete
}: TagAssignmentDialogProps) => {
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [assignedTags, setAssignedTags] = useState<Tag[]>([]);
  const [bulkTagData, setBulkTagData] = useState<{
    commonTags: Tag[];
    partialTags: Map<string, { tag: Tag; count: number }>;
  }>({ commonTags: [], partialTags: new Map() });
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [estimatedDeviceCount, setEstimatedDeviceCount] = useState(0);
  const [isAssigning, setIsAssigning] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [fallbackMessage, setFallbackMessage] = useState<string | null>(null);
  const [tagToRemove, setTagToRemove] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();

  // Helper functions for customer selection
  const addCustomer = (customerId: string) => {
    if (!selectedCustomers.includes(customerId)) {
      setSelectedCustomers([...selectedCustomers, customerId]);
    }
  };

  const removeCustomer = (customerId: string) => {
    setSelectedCustomers(selectedCustomers.filter(id => id !== customerId));
  };

  const getJunctionTable = () => {
    switch (resourceType) {
      case "device": return "device_tags";
      case "profile": return "profile_tags";
      case "application": return "application_tags";
      case "configuration": return "configuration_tags";
    }
  };

  const getResourceColumn = () => {
    switch (resourceType) {
      case "device": return "device_id";
      case "profile": return "profile_id";
      case "application": return "application_id";
      case "configuration": return "configuration_id";
    }
  };

  useEffect(() => {
    if (open && selectedCustomers.length > 0) {
      fetchTagsAndAssignments();
    }
  }, [open, resourceId, resourceIds, selectedCustomers]);

  useEffect(() => {
    if (open && resourceIds && resourceIds.length > 0) {
      fetchBulkTags();
    }
  }, [open, resourceIds]);

  const fetchTagsAndAssignments = async () => {
    setIsLoading(true);
    setConnectionError(false);
    setFallbackMessage(null);
    
    try {
      console.log(`[TagAssignment] Fetching tags for ${resourceType}${resourceIds ? ` (${resourceIds.length} resources)` : `: ${resourceId}`}`);
      
      // Fetch all available tags with customer filtering
      const { data: tags, error: tagsError } = await supabase
        .from("tags")
        .select("*")
        .order("name");

      if (tagsError) {
        console.error("[TagAssignment] Error fetching tags:", tagsError);
        throw tagsError;
      }

      setAvailableTags(tags || []);
      console.log(`[TagAssignment] Fetched ${tags?.length || 0} available tags`);

      // Only fetch assigned tags for single resource mode
      if (resourceId && !resourceIds) {
        const junctionTable = getJunctionTable();
        const resourceColumn = getResourceColumn();
        
        try {
          const { data: assignedData, error: assignedError } = await supabase
            .from(junctionTable as any)
            .select(`
              tags (
                id,
                name,
                description,
                color,
                type,
                customers
              )
            `)
            .eq(resourceColumn, resourceId);

          if (assignedError) throw assignedError;

          const assigned = assignedData?.map((item: any) => item.tags).filter(Boolean) || [];
          setAssignedTags(assigned);
          console.log(`[TagAssignment] Found ${assigned.length} already assigned tags`);
        } catch (err: any) {
          const isUuidErr = err?.code === '22P02' || /invalid input syntax for type uuid/i.test(err?.message || '');
          if ((resourceType === 'profile' || resourceType === 'application') && isUuidErr) {
            // Profiles and Applications use string IDs; show no assigned tags instead of error banner
            setAssignedTags([]);
            console.log(`[TagAssignment] ${resourceType} uses string ID, showing no assigned tags`);
          } else {
            console.error("[TagAssignment] Error fetching assigned tags:", err);
            throw err;
          }
        }
      } else {
        setAssignedTags([]);
      }

    } catch (error: any) {
      console.error("[TagAssignment] Failed to fetch tags:", error);
      setConnectionError(true);
      
      const errorMessage = error?.message?.includes('network') || error?.code === 'NETWORK_ERROR'
        ? "Network connection issue. Please check your internet connection and try again."
        : "Failed to fetch tags. Please refresh the page and try again.";
      
      // Show both toast and fallback message
      toast({
        title: "Connection Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      setFallbackMessage(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBulkTags = async () => {
    if (!resourceIds || resourceIds.length === 0) return;
    
    setIsLoading(true);
    try {
      console.log(`[TagAssignment] Fetching tags for ${resourceIds.length} resources in bulk mode`);
      
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      const allTagsPromises = resourceIds.map(async (resId) => {
        try {
          const { data: assignedData, error } = await supabase
            .from(junctionTable as any)
            .select(`
              tags (
                id,
                name,
                description,
                color,
                type,
                customers
              )
            `)
            .eq(resourceColumn, resId);

          if (error) throw error;
          return assignedData?.map((item: any) => item.tags).filter(Boolean) || [];
        } catch (err: any) {
          const isUuidErr = err?.code === '22P02' || /invalid input syntax for type uuid/i.test(err?.message || '');
          if ((resourceType === 'profile' || resourceType === 'application') && isUuidErr) {
            return [];
          }
          console.error(`[TagAssignment] Error fetching tags for ${resId}:`, err);
          return [];
        }
      });
      
      const allTagsArrays = await Promise.all(allTagsPromises);
      
      const tagCounts = new Map<string, { tag: Tag; count: number }>();
      
      allTagsArrays.forEach((tags) => {
        tags.forEach((tag) => {
          const existing = tagCounts.get(tag.id);
          if (existing) {
            existing.count++;
          } else {
            tagCounts.set(tag.id, { tag, count: 1 });
          }
        });
      });
      
      const common: Tag[] = [];
      const partial = new Map<string, { tag: Tag; count: number }>();
      
      tagCounts.forEach((value, key) => {
        if (value.count === resourceIds.length) {
          common.push(value.tag);
        } else {
          partial.set(key, value);
        }
      });
      
      setBulkTagData({ commonTags: common, partialTags: partial });
      console.log(`[TagAssignment] Found ${common.length} common tags and ${partial.size} partial tags`);
      
    } catch (error) {
      console.error("[TagAssignment] Error fetching bulk tags:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTagSelection = (tagId: string, checked: boolean) => {
    if (checked) {
      setSelectedTagIds([...selectedTagIds, tagId]);
    } else {
      setSelectedTagIds(selectedTagIds.filter(id => id !== tagId));
    }
  };

  const handleAssignTags = () => {
    if (selectedTagIds.length === 0) {
      toast({
        title: "No tags selected",
        description: "Please select at least one tag to assign.",
        variant: "destructive",
      });
      return;
    }
    setEstimatedDeviceCount(Math.floor(Math.random() * 50) + 10); // Mock estimation
    setShowConfirmation(true);
  };

  const confirmAssignment = async () => {
    setIsAssigning(true);
    setIsLoading(true);
    
    try {
      // Build array of resource IDs (single or multiple)
      const targetResourceIds = resourceIds && resourceIds.length > 0 
        ? resourceIds 
        : [resourceId!];
      
      console.log(`[TagAssignment] Starting assignment of ${selectedTagIds.length} tags to ${targetResourceIds.length} ${resourceType}(s)`);
      
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      // Check if user has permission to assign tags to this resource
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setShowConfirmation(false);
        throw new Error("Authentication required. Please log in to assign tags.");
      }

      // Build insert data for all resource IDs
      const insertData = targetResourceIds.flatMap(resId => 
        selectedTagIds.map(tagId => ({
          [resourceColumn as string]: resId,
          tag_id: tagId
        }))
      );
      
      console.log("[TagAssignment] Attempting to insert:", insertData);
      
      // Add retry logic with exponential backoff
      let success = false;
      let lastError = null;
      
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { error } = await supabase
            .from(junctionTable as any)
            .insert(insertData);

          if (!error) {
            success = true;
            break;
          }
          
          lastError = error;
          console.warn(`[TagAssignment] Attempt ${attempt} failed:`, error);
          
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        } catch (retryError) {
          lastError = retryError;
          console.warn(`[TagAssignment] Attempt ${attempt} exception:`, retryError);
        }
      }

      if (!success && lastError) {
        console.error("[TagAssignment] All attempts failed. Final error:", lastError);
        setShowConfirmation(false);
        
        if (lastError.code === '42501') {
          throw new Error("Insufficient permissions. You need proper authorization to assign tags to this resource.");
        }
        if (lastError.code === '23505') {
          throw new Error("Some tags are already assigned to this resource. Please refresh and try again.");
        }
        throw new Error(lastError.message || "Database operation failed after multiple attempts.");
      }

      // Success - update UI state
      const newlyAssigned = availableTags.filter(tag => selectedTagIds.includes(tag.id));
      const assignedTagNames = newlyAssigned.map(tag => tag.name).join(', ');
      
      setAssignedTags([...assignedTags, ...newlyAssigned]);
      setSelectedTagIds([]);
      setShowConfirmation(false);
      setRetryCount(0);
      
      console.log(`[TagAssignment] Successfully assigned ${selectedTagIds.length} tags to ${targetResourceIds.length} resources`);
      
      // Enhanced success notification
      const deviceCount = targetResourceIds.length;
      const tagCount = selectedTagIds.length;
      toast({
        title: "✅ Tags Assigned Successfully",
        description: `${tagCount} tag(s) assigned to ${deviceCount} ${resourceType}(s). Deployment initiated.`,
      });
      
      // Show fallback message as well
      setFallbackMessage(`Successfully assigned tags: ${assignedTagNames}`);
      setTimeout(() => setFallbackMessage(null), 5000);
      
      onAssignmentComplete?.();
      onComplete?.();
      
    } catch (error: any) {
      console.error("[TagAssignment] Assignment failed:", error);
      setShowConfirmation(false);
      setRetryCount(prev => prev + 1);
      
      const isNetworkError = error?.message?.includes('network') || error?.code === 'NETWORK_ERROR';
      const errorTitle = isNetworkError ? "🔌 Connection Error" : "❌ Assignment Failed";
      const errorMessage = isNetworkError 
        ? "Network connection lost. Please check your connection and try again."
        : error.message || "Failed to assign tags. Please try again.";
      
      // Both toast and fallback message
      toast({
        title: errorTitle,
        description: errorMessage,
        variant: "destructive",
      });
      
      setFallbackMessage(`Error: ${errorMessage}`);
      setTimeout(() => setFallbackMessage(null), 8000);
      
    } finally {
      setIsAssigning(false);
      setIsLoading(false);
    }
  };

  const initiateRemoveTag = (tagId: string) => {
    const tag = assignedTags.find(t => t.id === tagId);
    if (tag) {
      setTagToRemove({ id: tagId, name: tag.name });
    }
  };

  const confirmRemoveTag = async () => {
    if (!tagToRemove) return;
    
    const tag = assignedTags.find(t => t.id === tagToRemove.id) || 
                bulkTagData.commonTags.find(t => t.id === tagToRemove.id) ||
                bulkTagData.partialTags.get(tagToRemove.id)?.tag;
    
    try {
      const targetResourceIds = resourceIds && resourceIds.length > 0 ? resourceIds : [resourceId!];
      console.log(`[TagAssignment] Removing tag ${tagToRemove.id} from ${targetResourceIds.length} ${resourceType}(s)`);
      
      const junctionTable = getJunctionTable();
      const resourceColumn = getResourceColumn();
      
      for (const resId of targetResourceIds) {
        const insertData: any = {};
        insertData[resourceColumn as string] = resId;
        insertData.tag_id = tagToRemove.id;
        
        const { error } = await supabase
          .from(junctionTable as any)
          .delete()
          .match(insertData);

        if (error) {
          console.error("[TagAssignment] Remove tag error:", error);
          throw error;
        }
      }

      setAssignedTags(assignedTags.filter(t => t.id !== tagToRemove.id));
      
      if (resourceIds && resourceIds.length > 0) {
        await fetchBulkTags();
      }
      
      console.log(`[TagAssignment] Successfully removed tag: ${tag?.name}`);
      
      toast({
        title: "Tag Removed",
        description: `"${tag?.name || 'Tag'}" has been removed from ${targetResourceIds.length} ${resourceType}(s).`,
      });
      
      setFallbackMessage(`Removed tag: ${tag?.name || 'Unknown'}`);
      setTimeout(() => setFallbackMessage(null), 3000);
      
    } catch (error: any) {
      console.error("[TagAssignment] Failed to remove tag:", error);
      
      const errorMessage = error?.message?.includes('network') 
        ? "Network error. Please check your connection and try again."
        : "Failed to remove tag. Please try again.";
      
      toast({
        title: "Removal Failed",
        description: errorMessage,
        variant: "destructive",
      });
      
      setFallbackMessage(`Error removing "${tag?.name || 'tag'}": ${errorMessage}`);
      setTimeout(() => setFallbackMessage(null), 5000);
    } finally {
      setTagToRemove(null);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    onAssignmentComplete?.();
    onComplete?.();
  };

  const getFilteredTags = () => {
    let filtered = availableTags.filter(tag => {
      // Filter by search query
      const matchesSearch = tag.name.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Filter by selected customers (if any customers are selected)
      const matchesCustomers = selectedCustomers.length === 0 || 
        selectedCustomers.some(customerId => tag.customers?.includes(customerId));
      
      return matchesSearch && matchesCustomers;
    });
    
    return filtered;
  };

  const filteredTags = getFilteredTags();
  const isTagAssigned = (tagId: string) => assignedTags.some(assigned => assigned.id === tagId);

  return (
    <>
      <Dialog open={open && !showConfirmation} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assign Tags to {resourceIds && resourceIds.length > 1 
                ? `${resourceIds.length} ${resourceType}s` 
                : resourceName}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Connection Error Alert */}
            {connectionError && (
              <Alert variant="destructive">
                <WifiOff className="h-4 w-4" />
                <AlertDescription>
                  Connection issues detected. Some features may not work properly. Please check your internet connection.
                </AlertDescription>
              </Alert>
            )}

            {/* Fallback Message */}
            {fallbackMessage && (
              <Alert variant={fallbackMessage.includes('Error') ? "destructive" : "default"}>
                <CheckCircle className="h-4 w-4" />
                <AlertDescription>{fallbackMessage}</AlertDescription>
              </Alert>
            )}

            {/* Currently Assigned Tags */}
            {(resourceId || (resourceIds && resourceIds.length > 0)) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {resourceId ? 'Currently Assigned Tags' : `Tags Across Selected ${resourceType}s`}
                </Label>
                
                {/* Single resource mode */}
                {resourceId && !resourceIds && (
                  <div className="border rounded-md p-3 bg-muted/30 min-h-[60px]">
                    {assignedTags.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-2">
                        No tags assigned yet
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {assignedTags.map(tag => (
                          <Badge 
                            key={tag.id} 
                            variant="secondary" 
                            className="flex items-center gap-2 pl-2 pr-1 py-1"
                          >
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: tag.color }}
                            />
                            <span className="text-sm">{tag.name}</span>
                            <Badge variant="outline" className="text-xs ml-1">
                              {tag.type}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10"
                              onClick={() => initiateRemoveTag(tag.id)}
                              title={`Remove ${tag.name}`}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Bulk mode */}
                {resourceIds && resourceIds.length > 0 && (
                  <div className="space-y-2">
                    {/* Common Tags */}
                    {bulkTagData.commonTags.length > 0 && (
                      <div className="border rounded-md p-3 bg-green-50 dark:bg-green-950/20">
                        <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-2">
                          Common Tags (on all {resourceIds.length} {resourceType}s)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {bulkTagData.commonTags.map(tag => (
                            <Badge 
                              key={tag.id} 
                              variant="secondary" 
                              className="flex items-center gap-2 pl-2 pr-1 py-1"
                            >
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="text-sm">{tag.name}</span>
                              <Badge variant="outline" className="text-xs ml-1">
                                {tag.type}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10"
                                onClick={() => initiateRemoveTag(tag.id)}
                                title={`Remove ${tag.name}`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Partial Tags */}
                    {bulkTagData.partialTags.size > 0 && (
                      <div className="border rounded-md p-3 bg-amber-50 dark:bg-amber-950/20">
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-2">
                          Partial Tags (on some {resourceType}s)
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Array.from(bulkTagData.partialTags.values()).map(({ tag, count }) => (
                            <Badge 
                              key={tag.id} 
                              variant="outline" 
                              className="flex items-center gap-2 pl-2 pr-1 py-1"
                            >
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: tag.color }}
                              />
                              <span className="text-sm">{tag.name}</span>
                              <Badge variant="secondary" className="text-xs ml-1">
                                {count}/{resourceIds.length}
                              </Badge>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4 p-0 ml-1 hover:bg-destructive/10"
                                onClick={() => initiateRemoveTag(tag.id)}
                                title={`Remove ${tag.name} from ${count} ${resourceType}(s)`}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Empty state */}
                    {bulkTagData.commonTags.length === 0 && bulkTagData.partialTags.size === 0 && (
                      <div className="border rounded-md p-3 bg-muted/30 min-h-[60px]">
                        <p className="text-xs text-muted-foreground text-center py-2">
                          No tags assigned to any of the selected {resourceType}s
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                <p className="text-xs text-muted-foreground">
                  {resourceId 
                    ? `Click the X button to remove a tag from this ${resourceType}.`
                    : `Click X to remove tags from all selected ${resourceType}s.`
                  }
                </p>
              </div>
            )}

            {/* Customer Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Filter by Customer</Label>
              <div className="space-y-2">
                <Select
                  value=""
                  onValueChange={(value) => addCustomer(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select customers to filter tags" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockCustomers
                      .filter(customer => !selectedCustomers.includes(customer.id))
                      .map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                
                {/* Selected Customers */}
                {selectedCustomers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedCustomers.map((customerId) => {
                      const customer = mockCustomers.find(c => c.id === customerId);
                      return customer ? (
                        <Badge
                          key={customerId}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {customer.name}
                          <button
                            type="button"
                            onClick={() => removeCustomer(customerId)}
                            className="ml-1 hover:bg-secondary-foreground/20 rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Select customers to view their available tags
              </p>
            </div>

            {/* Search Available Tags */}
            {selectedCustomers.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Available Tags</Label>
                <div className="relative mt-2">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search tags..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>
            )}

            {/* Available Tags List */}
            <ScrollArea className="h-64 border rounded-md p-2">
              <div className="space-y-2">
                {selectedCustomers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Please select one or more customers to view their available tags.</p>
                  </div>
                 ) : isLoading ? (
                   <div className="flex flex-col items-center justify-center py-8 space-y-2">
                     <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                     <p className="text-sm text-muted-foreground">Loading tags...</p>
                     {retryCount > 0 && (
                       <p className="text-xs text-muted-foreground">Attempt {retryCount + 1}</p>
                     )}
                   </div>
                ) : filteredTags.length > 0 ? (
                  filteredTags.map(tag => {
                    const isAssigned = isTagAssigned(tag.id);
                    const isSelected = selectedTagIds.includes(tag.id);
                    
                    return (
                      <div
                        key={tag.id}
                        className={`flex items-center justify-between p-3 rounded-md border ${
                          isAssigned ? 'bg-muted/30 border-muted' : 'hover:bg-muted/50 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          {!isAssigned && (
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) => handleTagSelection(tag.id, checked as boolean)}
                            />
                          )}
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: tag.color }}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{tag.name}</div>
                            {tag.description && (
                              <div className="text-xs text-muted-foreground">{tag.description}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAssigned && (
                            <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                              Already Assigned
                            </Badge>
                          )}
                          <Badge variant={tag.type === "dynamic" ? "default" : "secondary"} className="text-xs">
                            {tag.type}
                          </Badge>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    No tags match your filters
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssignTags}
              disabled={selectedTagIds.length === 0 || selectedCustomers.length === 0 || isLoading || connectionError}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Loading...
                </div>
              ) : (
                `Assign ${selectedTagIds.length > 0 ? `(${selectedTagIds.length})` : ''}`
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deployment Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Confirm Deployment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This will assign {selectedTagIds.length} tag(s) to <strong>{resourceName}</strong> and deploy the configuration to approximately <strong>{estimatedDeviceCount} devices</strong> that have these tags.
            </p>
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                This action will trigger immediate deployment to all affected devices. Make sure this is intentional.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">Selected tags:</Label>
              <div className="flex flex-wrap gap-1">
                {selectedTagIds.map(tagId => {
                  const tag = availableTags.find(t => t.id === tagId);
                  return tag ? (
                    <Badge key={tagId} variant="secondary" className="text-xs">
                      {tag.name}
                    </Badge>
                  ) : null;
                })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmation(false)}>
              Cancel
            </Button>
            <Button onClick={confirmAssignment} disabled={isLoading || isAssigning}>
              {isAssigning ? (
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 animate-pulse" />
                  Deploying Configuration...
                </div>
              ) : (
                "Deploy Configuration"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tag Removal Confirmation Dialog */}
      <AlertDialog open={!!tagToRemove} onOpenChange={(open) => !open && setTagToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Remove Tag?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Removing "{tagToRemove?.name}" from {resourceName} may affect configurations and deployments. This action cannot be undone.
              <br /><br />
              Do you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveTag} className="bg-destructive hover:bg-destructive/90">
              Remove Tag
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};